import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { riseIn } from '@shared/theme/animations';

/* Общие блоки страниц раздела Кадры: заголовок раздела, ссылка «назад»,
   сетка «основное + боковая колонка», список «подпись — значение». */

const HeaderWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(4)};
  margin-bottom: ${({ theme }) => theme.spacing(5)};
  animation: ${riseIn} 420ms ${({ theme }) => theme.motion.easeOut} both;
`;

const HeaderText = styled.div`
  min-width: 0;
  flex: 1 1 320px;
`;

export const KadryTitle = styled.h1`
  margin: 0;
  font-size: 22px;
  font-weight: 600;
  letter-spacing: -0.01em;
  overflow-wrap: anywhere;
`;

const Subtitle = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
  line-height: 1.5;
  margin-top: ${({ theme }) => theme.spacing(2)};
  max-width: 760px;
`;

export const KadryActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
`;

interface HeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
}

/** Заголовок раздела: название, пояснение и действия справа. */
export function KadryPageHeader({ title, subtitle, actions }: HeaderProps) {
  return (
    <HeaderWrap>
      <HeaderText>
        <KadryTitle>{title}</KadryTitle>
        {subtitle && <Subtitle>{subtitle}</Subtitle>}
      </HeaderText>
      {actions && <KadryActions>{actions}</KadryActions>}
    </HeaderWrap>
  );
}

export const KadryBackLink = styled(Link)`
  display: inline-block;
  margin-bottom: ${({ theme }) => theme.spacing(3)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-decoration: none;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
    border-radius: ${({ theme }) => theme.radius.xs};
  }
`;

/** Две колонки: основное содержимое и боковая панель; на узких — одна. */
export const KadrySplit = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(280px, 360px);
  gap: ${({ theme }) => theme.spacing(5)};
  align-items: start;

  @media (max-width: 1180px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const KadryStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(5)};
  min-width: 0;
`;

/** Заголовок карточки с действиями справа. */
export const KadryCardHead = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};

  & > h3 {
    margin: 0;
  }
`;

/** Список «подпись — значение». */
export const KadryMetaList = styled.dl`
  display: grid;
  grid-template-columns: minmax(120px, max-content) minmax(0, 1fr);
  gap: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(4)}`};
  margin: 0;
  font-size: 14px;

  dt {
    color: ${({ theme }) => theme.colors.text.secondary};
  }
  dd {
    margin: 0;
    color: ${({ theme }) => theme.colors.text.primary};
    overflow-wrap: anywhere;
  }

  @media (max-width: 520px) {
    grid-template-columns: minmax(0, 1fr);
    gap: ${({ theme }) => theme.spacing(1)};

    dd {
      margin-bottom: ${({ theme }) => theme.spacing(2)};
    }
  }
`;

/** Пояснение серым мелким шрифтом. */
export const KadryMuted = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

/** Пустое состояние внутри карточки. */
export const KadryEmpty = styled.div`
  padding: ${({ theme }) => `${theme.spacing(6)} ${theme.spacing(3)}`};
  text-align: center;
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.muted};
`;

/** Обёртка таблицы с горизонтальной прокруткой на узких экранах. */
export const KadryTableScroll = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin: ${({ theme }) => `0 -${theme.spacing(5)}`};
  padding: ${({ theme }) => `0 ${theme.spacing(5)}`};

  table {
    min-width: 640px;
  }
`;

export const KadryMono = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 12px;
`;

/** Отступ между баннером и следующим блоком. */
export const KadryBannerGap = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;
